import { Link } from "react-router-dom";
import { LifebuoyIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import useManagedSupportPricing from "../../hooks/useManagedSupportPricing";
import { pricingTheme, type PricingTone } from "./pricingTheme";

interface SetupFeePlan {
    id: string;
    displayName: string;
    setupFeePrices?: Array<{ currency: string; amount?: number | null; chatAmount?: number | null; callAmount?: number | null }> | null;
}

interface Props {
    tone: PricingTone;
    currency: "usd" | "inr";
    plans: SetupFeePlan[];
}

/** Setup fees are stored in major units on the plan, unlike usage rates. */
const formatMajor = (amount: number, currency: string) =>
    new Intl.NumberFormat(currency === "inr" ? "en-IN" : "en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        maximumFractionDigits: 0,
    }).format(amount);

export default function AddOnsSection({ tone, currency, plans }: Props) {
    const t = pricingTheme(tone);
    const { ready, agentPrice, supervisorPrice } = useManagedSupportPricing();

    const setupFees = plans
        .map((plan) => {
            const entry = plan.setupFeePrices?.find((p) => String(p.currency).toLowerCase() === currency);
            if (!entry) return null;
            const chat = Number(entry.chatAmount || 0);
            const call = Number(entry.callAmount || 0);
            const total = Number(entry.amount || 0);
            if (chat <= 0 && call <= 0 && total <= 0) return null;
            return { id: plan.id, name: plan.displayName, chat, call, total };
        })
        .filter((p): p is NonNullable<typeof p> => p !== null);

    return (
        <section aria-labelledby="pricing-add-ons" className={t.section}>
            <h3 id="pricing-add-ons" className={`text-xl font-bold ${t.heading}`}>
                Optional add-ons
            </h3>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className={t.card}>
                    <LifebuoyIcon className={`h-6 w-6 ${t.accent}`} aria-hidden="true" />
                    <h4 className={`mt-3 text-base font-semibold ${t.subheading}`}>Managed support agents</h4>
                    <p className={`mt-1 text-sm ${t.body}`}>Our trained agents answer your chats and calls, billed per seat each month.</p>
                    {ready && (
                        <p className={`mt-3 text-sm ${t.body}`}>
                            Agent <span className={`font-bold ${t.heading}`}>{agentPrice}</span> / month · Supervisor{" "}
                            <span className={`font-bold ${t.heading}`}>{supervisorPrice}</span> / month
                        </p>
                    )}
                    <Link to="/managed-support" className={`mt-3 inline-block text-sm font-semibold ${t.link}`}>
                        Learn more
                    </Link>
                </div>
                <div className={t.card}>
                    <WrenchScrewdriverIcon className={`h-6 w-6 ${t.accent}`} aria-hidden="true" />
                    <h4 className={`mt-3 text-base font-semibold ${t.subheading}`}>Done-for-you setup</h4>
                    <p className={`mt-1 text-sm ${t.body}`}>A one-time fee for our team to set up and tune your chat or voice agents. Choose it at checkout.</p>
                    {setupFees.length > 0 ? (
                        <ul className={`mt-3 space-y-1 text-sm ${t.body}`}>
                            {setupFees.map((fee) => (
                                <li key={fee.id}>
                                    <span className={t.subheading}>{fee.name}:</span>{" "}
                                    {fee.chat > 0 || fee.call > 0
                                        ? [fee.chat > 0 ? `chat ${formatMajor(fee.chat, currency)}` : null, fee.call > 0 ? `call ${formatMajor(fee.call, currency)}` : null]
                                              .filter(Boolean)
                                              .join(" · ")
                                        : formatMajor(fee.total, currency)}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={`mt-3 text-sm ${t.muted}`}>Quoted with your plan.</p>
                    )}
                </div>
            </div>
        </section>
    );
}
